import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Observable, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, map, skip, startWith, take, takeUntil, tap } from 'rxjs';
import { SafeLiftService } from 'src/app/services/app/safelift.service';
import { App_FormFieldInputNumber, App_FormFieldInputText, App_TrueField } from 'src/app/utils/interfaces';

@Component({
  selector: 'app-c-inputtext',
  templateUrl: './c-inputtext.component.html',
  styleUrls: ['./c-inputtext.component.scss'],
})
export class CInputtextComponent implements OnInit, OnDestroy {
  @Input({ required: true }) field!: App_FormFieldInputText | App_FormFieldInputNumber;
  @Input({ required: true }) cFormControl!: FormControl;
  @Input({ required: true }) blocName!: string;
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) disabledField!: boolean;
  @Input({ required: false }) noLabel: true | undefined;
  @Input({ required: false }) looksDisabled!: true | undefined;
  @Input() etatId?: number;
  @Output() triggerAlert = new EventEmitter<{
    blocName: string;
    fieldName: string;
    fieldFormControl: AbstractControl<any, any>;
    field: App_TrueField;
  }>();

  destroyComponent$: Subject<boolean> = new Subject();

  constructor(
    private safeLiftService: SafeLiftService
  ) { }

  ngOnInit() {
    this.addAlertSubscriptions();
    if (this.field.subType === 'input-number') {
      setTimeout(() => this.normalizeNumberValue(), 100);
    }
    if (this.field.valueIsResultOf !== undefined) {
      const operation = this.field.valueIsResultOf.operation;
      const constraints = this.field.valueIsResultOf.constraints;
      if (typeof operation === 'string') {
        const matches = operation.match(/\$[\w]+/g)
        if (Array.isArray(matches)) {
          if (
            Array.isArray(constraints)
            && constraints.length > 0
            && constraints.every(constraint => typeof constraint.champ === 'string' && constraint.champ !== this.field.name && typeof constraint.id === 'string' && typeof constraint.type === 'string' && typeof constraint.value === 'string')
            && matches.every(matchStr => constraints.find(constraint => constraint.id === matchStr))
          ) {
            const observables = constraints.reduce<(Observable<{ id: string, champ: string, valuetoReplace: string, constraintValue: any }> | undefined)[]>((acc, constraint) => {
              const field = this.safeLiftService.findFieldInFormEntity(this.form, constraint.champ)
              if (field instanceof AbstractControl) {
                return [
                  ...acc,
                  field.valueChanges.pipe(
                    startWith(field.value),
                    distinctUntilChanged()
                  ).pipe(map(value => ({
                    id: constraint.id,
                    champ: constraint.champ,
                    valuetoReplace: constraint.value,
                    constraintValue: value,
                  })))
                ]
              }
              return [...acc, undefined]
            }, []);
            if (observables.every(obs => obs instanceof Observable)) {
              const obs$ = combineLatest(
                observables.map(_ => (_ as Observable<{ id: string, champ: string, valuetoReplace: string, constraintValue: any }>)/*.pipe(tap(x => console.log('x', x)))*/)
              ).pipe(
                takeUntil(this.destroyComponent$),
                map((ctrlsFields) => {
                  try {
                    const arrayOfTransformedValues: { id: string, champ: string, value: string }[] = []
                    for (const ctrlField of ctrlsFields) {
                      if (typeof ctrlField.constraintValue === 'string' || typeof ctrlField.constraintValue === 'number') {
                        const asNumber = Number(ctrlField.constraintValue)
                        if (!Number.isNaN(asNumber)) {
                          const valuetoPush = this.replaceValue(asNumber, ctrlField.valuetoReplace, ctrlField.id)
                          arrayOfTransformedValues.push({
                            id: ctrlField.id,
                            champ: ctrlField.champ,
                            value: valuetoPush,
                          })
                        } else {
                          return null;
                        }
                      } else {
                        return null;
                      }
                    }
                    console.log('[valueIsResultOf] Champs concernés par le calcul:', {
                      champDestination: this.field.name,
                      operation: operation,
                      champsSources: arrayOfTransformedValues.map(v => ({
                        id: v.id,
                        champ: v.champ,
                        valeur: v.value
                      }))
                    });
                    const evaluatedOperation = this.evaluateOperation(operation, arrayOfTransformedValues);
                    return evaluatedOperation;
                  } catch (error) {
                    return null;
                  }
                }),
                distinctUntilChanged(),
              );
              obs$
              .pipe(take(1))
              .subscribe(res => {
                this.cFormControl.setValue(this.normalizeNumber(res));
              });
              obs$
              .pipe(skip(1))
              .subscribe(res => {
                this.cFormControl.setValue(this.normalizeNumber(res));
                this.cFormControl.markAsDirty();
              });
            }
          }
        } else {
          const evaluatedOperation = this.evaluateOperation(operation, []);
          this.cFormControl.setValue(this.normalizeNumber(evaluatedOperation));
          this.cFormControl.markAsDirty();
        }
      }
    }
  }

  private parseNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const str = typeof value === 'string' ? value.replace(/,/g, '.') : String(value);
    const num = Number(str);
    return Number.isFinite(num) ? num : null;
  }

  /** Normalise une valeur numérique (ex. 5.00000 → 5) pour l’affichage. */
  private normalizeNumber(value: any): any {
    const num = this.parseNumber(value);
    if (num === null) return value;
    const str = num.toFixed(10).replace(/\.?0+$/, '');
    return str === '' ? num : parseFloat(str);
  }

  /** Met à jour le FormControl avec la valeur numérique normalisée si c’est un champ number. Appelé au chargement et au blur. */
  normalizeNumberValue(): void {
    if (this.field.subType !== 'input-number') return;
    const value = this.cFormControl.value;
    const num = this.parseNumber(value);
    if (num === null) return;
    const normalized = this.normalizeNumber(num);
    if (String(normalized) !== String(value)) {
      this.cFormControl.setValue(normalized, { emitEvent: false });
    }
  }

  ngOnDestroy() {
    this.destroyComponent$.next(true);
  }

  addAlertSubscriptions() {
    const checkValidity = this.field.checkValidity;
    if (checkValidity !== undefined) {
      const validators = Array.isArray(checkValidity.validators)
      ? checkValidity.validators
      : [];
      const asyncValidators = Array.isArray(checkValidity.asyncValidators)
      ? checkValidity.asyncValidators
      : [];
      const allValidators = [
        ...validators,
        ...asyncValidators
      ];
      const validatorsWithTriggerAlert = allValidators.filter(validator => validator.triggerAlert);
      if (validatorsWithTriggerAlert.length > 0) {
        this.cFormControl.statusChanges.pipe(
          takeUntil(this.destroyComponent$),          
          debounceTime(200),
          filter(status => status === 'INVALID'),
          map(_ => {
            const errors = this.cFormControl.errors;
            if (errors !== null && (validatorsWithTriggerAlert.find(validator => errors[validator.type]) || (errors['err'] !== undefined && errors['err'][`type`] !== undefined && validatorsWithTriggerAlert.find(validator => errors['err'][`type`] === validator.type)))) {
              return errors;
            }
            return null;
          }),
          filter(_ => _ !== null),
        ).subscribe(errors => {
          this.triggerAlert.emit({
            blocName: this.blocName,
            fieldName: this.field.name,
            fieldFormControl: this.cFormControl,
            field: this.field,
          });
        })
      }
    }
  }

  replaceValue(value: number, valueToReplace: string, key: string) {
    const valueReplaced = valueToReplace.replace(key, value.toString());
    return valueReplaced;
  }

  evaluateOperation(operation: string, arrayOfTransformedValues: { id: string, champ: string, value: string }[]) {
    const operationReplaced = operation.replace(/\$[\w]+/g, str => {
      const found = arrayOfTransformedValues.find(v => v?.id === str);
      if (found) {
        return found.value
      }
      return str;
    });
    try {
      const evaluated = eval(operationReplaced);
      if (!Number.isNaN(evaluated) && evaluated !== Infinity && evaluated !== (-Infinity)) {
        return evaluated;
      } else {
        return null;
      }
    } catch (error) {
      return null
    }
  }

  /** Valeur formatée pour l’affichage (sans zéros décimaux inutiles, ex. 5 au lieu de 5,00000). */
  get displayedValue(): any {
    const value = this.cFormControl.value;
    const num = this.parseNumber(value);
    if (num === null) return value;
    const precision = (this.field as any).precision;
    const decimals = typeof precision === 'number' ? precision : 10;
    const formatted = num.toFixed(decimals);
    return formatted.replace(/\.?0+$/, '');
  }

}
